import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { format, formatDistanceToNowStrict } from "date-fns";
import { Trash2 } from "lucide-react";
import { EmptyState } from "@/components/shared/empty-state";
import { ConfirmDialog } from "@/components/shared/confirm-dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { useClientNotes, useCreateNote, useDeleteNote } from "@/features/clients/api";
import { useClientId } from "@/features/clients/detail/use-client-id";
import { HubToolbar } from "@/features/clients/detail/hub-toolbar";
import { noteFormSchema, type NoteFormValues } from "@/features/clients/schema";

export function ClientNotesPage() {
  const clientId = useClientId();
  const { data: notes = [], isLoading } = useClientNotes(clientId);
  const createNote = useCreateNote(clientId);
  const deleteNote = useDeleteNote(clientId);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const form = useForm<NoteFormValues>({
    resolver: zodResolver(noteFormSchema),
    defaultValues: { body: "" },
  });

  const onSubmit = form.handleSubmit(async (values) => {
    await createNote.mutateAsync(values);
    form.reset({ body: "" });
  });

  return (
    <div>
      <HubToolbar title="Notes" description="Internal log — visible to your team only." />

      <div className="mx-auto max-w-3xl space-y-3 p-4">
      <form onSubmit={onSubmit} className="rounded-lg border border-border bg-background p-2">
        <Textarea
          {...form.register("body")}
          placeholder="Log a call, a price discussion, a complaint…"
          rows={2}
          className="border-0 bg-transparent px-1.5 focus-visible:ring-0"
          onKeyDown={(e) => {
            if ((e.metaKey || e.ctrlKey) && e.key === "Enter") void onSubmit();
          }}
        />
        <div className="flex items-center justify-between gap-2 px-1.5 pt-1">
          <span className="text-2xs text-muted-foreground">
            {form.formState.errors.body?.message ?? "Ctrl + Enter to post"}
          </span>
          <Button type="submit" size="xs" disabled={createNote.isPending}>
            Add note
          </Button>
        </div>
      </form>

      {isLoading ? (
        <p className="py-8 text-center text-xs text-muted-foreground">Loading…</p>
      ) : notes.length === 0 ? (
        <EmptyState title="No notes yet" description="The first note will appear here." />
      ) : (
        <ol className="relative space-y-0 border-l border-border pl-4">
          {notes.map((note) => (
            <li key={note.id} className="group relative py-2.5">
              <span className="absolute -left-[1.3125rem] top-4 size-1.5 rounded-full bg-border-strong" />
              <div className="flex items-start justify-between gap-2">
                <p className="whitespace-pre-wrap text-sm leading-relaxed">{note.body}</p>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon-sm"
                  aria-label="Delete note"
                  className="shrink-0 opacity-0 transition-opacity group-hover:opacity-100 focus-visible:opacity-100 hover:text-destructive"
                  onClick={() => setDeletingId(note.id)}
                >
                  <Trash2 />
                </Button>
              </div>
              <time
                className="text-2xs text-muted-foreground"
                dateTime={note.created_at}
                title={format(new Date(note.created_at), "dd MMM yyyy HH:mm")}
              >
                {formatDistanceToNowStrict(new Date(note.created_at), { addSuffix: true })}
              </time>
            </li>
          ))}
        </ol>
      )}

      <ConfirmDialog
        open={Boolean(deletingId)}
        onOpenChange={(v) => !v && setDeletingId(null)}
        title="Delete note?"
        confirmLabel="Delete"
        destructive
        loading={deleteNote.isPending}
        onConfirm={async () => {
          if (!deletingId) return;
          await deleteNote.mutateAsync(deletingId);
          setDeletingId(null);
        }}
      />
      </div>
    </div>
  );
}
